import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { UserApiTokenDto } from '@app/data/dto';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { DatePicker } from '@app/shared/components/date-picker/date-picker';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';

type TokenCreateModel = {
  description: string;
  expireAt: string;
};

@Component({
  selector: 'app-api-token',
  imports: [AppIcon, CommonModule, FormsModule, TranslatePipe, CdkTextareaAutosize, DatePicker],
  templateUrl: './api-token.component.html',
  styleUrl: './api-token.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiTokenComponent {
  private readonly today = new Date();

  readonly tokenCreateModel = input.required<TokenCreateModel>();
  readonly generatedTokenValue = input<string>('');
  readonly apiTokens = input<UserApiTokenDto[]>([]);

  protected readonly createModalOpen = signal(false);
  protected readonly minExpireDate = new Date();
  protected readonly searchTerm = signal('');
  protected readonly statusFilter = signal<'all' | 'active' | 'expired'>('all');

  protected readonly filteredTokens = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const status = this.statusFilter();

    return this.apiTokens().filter((token) => {
      const tokenName = (token.description ?? '').toLowerCase();
      const matchesSearch = term.length === 0 || tokenName.includes(term);

      const isExpired = this.isTokenExpired(token);
      const matchesStatus = status === 'all'
        || (status === 'active' && !isExpired)
        || (status === 'expired' && isExpired);

      return matchesSearch && matchesStatus;
    });
  });

  readonly createToken = output<void>();
  readonly rotateToken = output<string>();
  readonly deleteToken = output<string>();
  readonly cancelled = output<void>();

  protected onSearchTermChange(value: string): void {
    this.searchTerm.set(value);
  }

  protected onStatusFilterChange(value: 'all' | 'active' | 'expired'): void {
    this.statusFilter.set(value);
  }

  protected isTokenExpired(token: UserApiTokenDto): boolean {
    const expireAt = new Date(token.expireAt);
    if (Number.isNaN(expireAt.getTime())) {
      return false;
    }

    return expireAt.getTime() < Date.now();
  }

  protected getExpireInDays(token: UserApiTokenDto): number | null {
    const expireAt = new Date(token.expireAt);
    if (Number.isNaN(expireAt.getTime())) {
      return null;
    }

    const oneDayMs = 24 * 60 * 60 * 1000;
    return Math.ceil((expireAt.getTime() - this.today.getTime()) / oneDayMs);
  }

  protected openCreateModal(): void {
    this.createModalOpen.set(true);
  }

  protected closeCreateModal(): void {
    this.createModalOpen.set(false);
  }

  protected onCreateBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeCreateModal();
    }
  }

  protected onCreateToken(): void {
    this.createToken.emit();
    this.closeCreateModal();
  }

  protected getExpireAtDate(): Date | null {
    const value = this.tokenCreateModel().expireAt;
    if (!value) {
      return null;
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  protected onExpireAtChange(nextValue: Date | null): void {
    if (!nextValue || Number.isNaN(nextValue.getTime())) {
      this.tokenCreateModel().expireAt = '';
      return;
    }

    this.tokenCreateModel().expireAt = this.toIsoDate(nextValue);
  }

  private toIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

